import { z } from 'zod';
// Incluye 'root'/'CEO' a propósito: el rechazo de asignarlos vía API es una
// regla de negocio (SRS-01 §9; Prompt AF-4(RH) para 'CEO') enforced en la
// capa de servicio vía ForbiddenError, no un error de validación Zod — así
// GET /usuarios sigue tipando/deserializando correctamente esas filas.
// Prompt BJ-04 — 'marketing' agregado: equivalente exacto de 'digital' en
// todos los guards (ver clientes.service.ts y BJ-01/BJ-02).
// 'ads_manager' agregado (decisión 10-oct): equivalente exacto de 'kam' en
// todos los guards y en PERMISSIONS (ver permissions.ts).
// 'gerente_comercial' agregado (decisión 15-oct): Ceci, gestora de cartera.
const ROLES = ['root', 'head', 'analista', 'kam', 'ads_manager', 'comercial', 'gerente_comercial', 'digital', 'CEO', 'marketing'];
// Prompt BJ-04 — etiqueta de rol centralizada (antes duplicada inline en
// UsuariosPage.tsx). Única fuente para mostrar el nombre legible de un rol.
export const ROL_LABEL = {
    root: 'Root',
    head: 'Head',
    analista: 'Analista',
    kam: 'KAM',
    ads_manager: 'Ads manager',
    comercial: 'Comercial',
    gerente_comercial: 'Gerente Comercial',
    digital: 'Digital',
    CEO: 'CEO',
    marketing: 'Marketing',
};
// Prompt BJ-04 — función centralizada de formateo del label de puesto/rol de
// un usuario. 100% visual, CERO efecto en permisos. El asterisco se agrega
// SIEMPRE al puesto (nunca al rol): si el usuario no tiene `puesto` capturado,
// el fallback es el nombre del rol SIN asterisco, sin importar `es_interno`.
export function formatUsuarioLabel(usuario) {
    const puesto = usuario.puesto?.trim();
    if (!puesto)
        return ROL_LABEL[usuario.rol] ?? usuario.rol;
    return usuario.es_interno ? `${puesto}*` : puesto;
}
const TIPOS_EMPLEO = ['tiempo_completo', 'medio_tiempo', 'honorarios'];
// ── crearUsuarioSchema ────────────────────────────────────────────────────────
//
// Prompt BB-3(RH) — extendido con los campos organizacionales/RH de AB (ya
// existían en la tabla desde entonces, solo nunca se habían expuesto en el
// alta — únicamente en el PATCH). Ningún campo nuevo en `usuarios`, esto
// solo permite capturarlos en el mismo formulario unificado de creación en
// vez de crear y luego editar. Misma regla cruzada es_aprobador_final/
// aprobador_permisos_id que actualizarUsuarioSchema — validada en el
// servicio (crearUsuario()), no aquí, mismo criterio que el resto del archivo.
export const crearUsuarioSchema = z.object({
    nombre: z.string().min(1).max(120),
    correo: z
        .string()
        .email()
        .max(120)
        .refine(v => v.toLowerCase().endsWith('@leadsphere.com'), {
        message: 'El correo debe pertenecer al dominio @leadsphere.com',
    }),
    rol: z.enum(ROLES),
    // Puesto/designación real (ej. "Analista Ads") — independiente de `rol`
    // (que determina permisos/funciones, no el título mostrado).
    puesto: z.string().max(100).optional(),
    area: z.string().max(50).optional(),
    // Prompt BJ-04 — 100% visual, ver formatUsuarioLabel(). root nunca puede ser
    // intern, pero eso ya está garantizado aquí arriba (rol='root' se rechaza
    // antes de llegar a este campo), no requiere regla cruzada adicional.
    es_interno: z.boolean().optional(),
    fecha_ingreso: z.string().date().nullable().optional(),
    // Opcional — solo alimenta el saldo paralelo de nómina (Años de servicio).
    fecha_ingreso_nomina: z.string().date().nullable().optional(),
    fecha_nacimiento: z.string().date().nullable().optional(),
    reports_to: z.string().uuid().nullable().optional(),
    es_aprobador_final: z.boolean().optional(),
    aprobador_permisos_id: z.string().uuid().nullable().optional(),
    tipo_empleo: z.enum(TIPOS_EMPLEO).optional(),
    celular: z.string().max(20).nullable().optional(),
    correo_personal: z.string().email().max(150).nullable().optional(),
    contacto_emergencia_nombre: z.string().max(150).nullable().optional(),
    contacto_emergencia_telefono: z.string().max(20).nullable().optional(),
    contacto_emergencia_relacion: z.string().max(50).nullable().optional(),
});
// ── actualizarUsuarioSchema ───────────────────────────────────────────────────
//
// Prompt AB (RH/Vacaciones, fundacional) — extensión con campos
// organizacionales. Nombres de campo en snake_case (no camelCase como el
// pseudocódigo original del prompt) para seguir la convención ya establecida
// en el resto de la API (solicitante_id, nivel_servicio_es_externo, etc.).
//
// esAprobadorFinal/aprobadorPermisosId, auto-referencia y anti-ciclos de
// reports_to NO se validan aquí — dependen del estado ya guardado en BD
// (esto es un PATCH parcial) y requieren consultar la tabla, así que la
// regla de negocio vive en actualizarUsuario() (usuarios.service.ts), no en
// Zod. Ver ahí para el detalle exacto.
export const actualizarUsuarioSchema = z.object({
    nombre: z.string().min(1).max(120).optional(),
    rol: z.enum(ROLES).optional(),
    puesto: z.string().max(100).nullable().optional(),
    area: z.string().max(50).nullable().optional(),
    es_interno: z.boolean().optional(),
    fecha_ingreso: z.string().date().nullable().optional(),
    // Opcional — solo alimenta el saldo paralelo de nómina (Años de servicio).
    fecha_ingreso_nomina: z.string().date().nullable().optional(),
    fecha_nacimiento: z.string().date().nullable().optional(),
    reports_to: z.string().uuid().nullable().optional(),
    es_aprobador_final: z.boolean().optional(),
    aprobador_permisos_id: z.string().uuid().nullable().optional(),
    tipo_empleo: z.enum(TIPOS_EMPLEO).optional(),
    celular: z.string().max(20).nullable().optional(),
    correo_personal: z.string().email().max(150).nullable().optional(),
    contacto_emergencia_nombre: z.string().max(150).nullable().optional(),
    contacto_emergencia_telefono: z.string().max(20).nullable().optional(),
    contacto_emergencia_relacion: z.string().max(50).nullable().optional(),
});
// ── toggleActivoSchema ────────────────────────────────────────────────────────
export const toggleActivoSchema = z.object({
    activo: z.boolean(),
});
//# sourceMappingURL=usuarios.js.map